import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";

interface AuditEntry {
  id: number;
  createdAt: string;
  actionType: string;
  entityType: string;
  entityUuid: string | null;
  metadata: Record<string, unknown>;
  prevHash: string | null;
  entryHash: string;
  user: { uuid: string; fullName: string; email: string; erased: boolean } | null;
}

interface EntityHistoryPanelProps {
  entityType: string;
  entityUuid: string;
  title?: string;
}

const ACTION_LABEL: Record<string, string> = {
  EVIDENCE_CREATED: "Created",
  EVIDENCE_UPDATED: "Updated",
  EVIDENCE_DELETED: "Deleted",
  EVIDENCE_FILE_ADDED: "File added",
  EVIDENCE_FILE_REMOVED: "File removed",
  EVIDENCE_TRANSITIONED: "Status changed",
  EVIDENCE_LINKED_TO_REQUIREMENT: "Linked to clause",
  EVIDENCE_UNLINKED_FROM_REQUIREMENT: "Unlinked from clause",
  NC_RAISED: "Raised",
  NC_TRANSITIONED: "Status changed",
  NC_UPDATED: "Updated",
  CAPA_CREATED: "Action created",
  CAPA_TRANSITIONED: "Action status changed",
  CAPA_UPDATED: "Action updated",
  MEMBER_INVITED: "Invited",
  MEMBER_ACCEPTED_INVITATION: "Accepted invitation",
  MEMBER_DECLINED_INVITATION: "Declined invitation",
  MEMBER_REMOVED: "Removed",
};

const formatDateTimeUk = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

export function EntityHistoryPanel({
  entityType,
  entityUuid,
  title = "History",
}: EntityHistoryPanelProps) {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    api<{ entries: AuditEntry[] }>(`/entity-history/${entityType}/${entityUuid}`)
      .then((res) => {
        if (!cancelled) setEntries(res.entries);
      })
      .catch((err) => {
        if (!cancelled) setError(formatApiError(err)[0] ?? "Failed to load history");
      });
    return () => {
      cancelled = true;
    };
  }, [entityType, entityUuid]);

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section>
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </h2>
      {error ? (
        <p className="mt-2 text-xs text-red-700 dark:text-red-200">{error}</p>
      ) : entries === null ? (
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No history yet.</p>
      ) : (
        <ol className="mt-2 space-y-1">
          {entries.map((entry) => {
            const isOpen = expanded.has(entry.id);
            const userDisplay = entry.user
              ? entry.user.erased
                ? `Anonymous (erased)`
                : entry.user.fullName
              : "Anonymous";
            return (
              <li
                key={entry.id}
                className="rounded border border-slate-200 bg-white text-xs dark:border-slate-700 dark:bg-slate-800"
              >
                <button
                  type="button"
                  onClick={() => toggle(entry.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40"
                >
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {ACTION_LABEL[entry.actionType] ?? entry.actionType}
                    </span>
                    <span className="ml-2 text-slate-500 dark:text-slate-400">
                      by {userDisplay}
                    </span>
                  </div>
                  <span className="text-slate-500 dark:text-slate-400">
                    {formatDateTimeUk(entry.createdAt)}
                  </span>
                </button>
                {isOpen ? (
                  <div className="border-t border-slate-200 bg-slate-50 p-3 font-mono text-[11px] dark:border-slate-700 dark:bg-slate-900">
                    <pre className="whitespace-pre-wrap break-words text-slate-700 dark:text-slate-300">
                      {JSON.stringify(entry.metadata, null, 2)}
                    </pre>
                    <p className="mt-2 text-[10px] text-slate-400">
                      Hash: {entry.entryHash.slice(0, 24)}…
                    </p>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
